/**
 * StatusPoller - Utility for polling campaign/job status
 *
 * Extracted from campaign_detail.html inline scripts.
 * Provides a reusable polling mechanism for status updates.
 *
 * @example
 * const poller = new StatusPoller('/api/campaign/123/status/', {
 *   interval: 2000,
 *   onUpdate: (data) => console.log('Status:', data.status),
 *   onComplete: (data) => console.log('Done!'),
 *   onError: (error) => console.error(error),
 * });
 * poller.start();
 */

export class StatusPoller {
  /**
   * @param {string} url - URL to poll
   * @param {object} options - Configuration options
   * @param {number} [options.interval=2000] - Polling interval in ms
   * @param {number} [options.maxAttempts=0] - Max attempts (0 = unlimited)
   * @param {string[]} [options.completeStatuses] - Statuses that indicate completion
   * @param {string[]} [options.errorStatuses] - Statuses that indicate error
   * @param {Function} [options.onUpdate] - Callback on each poll
   * @param {Function} [options.onComplete] - Callback when complete
   * @param {Function} [options.onError] - Callback on error
   */
  constructor(url, options = {}) {
    this.url = url;
    this.interval = options.interval || 2000;
    this.maxAttempts = options.maxAttempts || 0;
    this.completeStatuses = options.completeStatuses || ['completed', 'sent', 'done'];
    this.errorStatuses = options.errorStatuses || ['failed', 'error', 'cancelled'];

    this.onUpdate = options.onUpdate || (() => {});
    this.onComplete = options.onComplete || (() => {});
    this.onError = options.onError || console.error;

    this.isPolling = false;
    this.attempts = 0;
    this.timeoutId = null;
    this.abortController = null;
  }

  /**
   * Start polling
   */
  start() {
    if (this.isPolling) {
      console.warn('[StatusPoller] Already polling');
      return;
    }

    this.isPolling = true;
    this.attempts = 0;
    this.poll();
  }

  /**
   * Stop polling
   */
  stop() {
    this.isPolling = false;

    if (this.timeoutId) {
      clearTimeout(this.timeoutId);
      this.timeoutId = null;
    }

    if (this.abortController) {
      this.abortController.abort();
      this.abortController = null;
    }
  }

  /**
   * Execute a single poll
   */
  async poll() {
    if (!this.isPolling) return;

    this.attempts++;

    // Check max attempts
    if (this.maxAttempts > 0 && this.attempts > this.maxAttempts) {
      this.stop();
      this.onError(new Error('Max polling attempts reached'));
      return;
    }

    try {
      // Create abort controller for this request
      this.abortController = new AbortController();

      const response = await fetch(this.url, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        credentials: 'same-origin',
        signal: this.abortController.signal,
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();

      // Call update callback
      this.onUpdate(data);

      // Check if complete
      const status = data.status?.toLowerCase() || '';

      if (this.completeStatuses.includes(status)) {
        this.stop();
        this.onComplete(data);
        return;
      }

      // Check if error
      if (this.errorStatuses.includes(status)) {
        this.stop();
        this.onError(new Error(`Status: ${status}`), data);
        return;
      }

      // Schedule next poll
      if (this.isPolling) {
        this.timeoutId = setTimeout(() => this.poll(), this.interval);
      }

    } catch (error) {
      if (error.name === 'AbortError') {
        // Polling was stopped, ignore
        return;
      }

      console.error('[StatusPoller] Error:', error);

      // Retry on network errors (up to 3 consecutive failures)
      if (this.attempts < 3) {
        this.timeoutId = setTimeout(() => this.poll(), this.interval * 2);
      } else {
        this.stop();
        this.onError(error);
      }
    }
  }

  /**
   * Get polling state
   * @returns {object}
   */
  getState() {
    return {
      isPolling: this.isPolling,
      attempts: this.attempts,
      url: this.url,
    };
  }
}

export default StatusPoller;
